import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import DraggablePanel from "./DraggablePanel";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../ui/Collapsible";
import Checkbox from "../ui/Checkbox";
import FieldLabel from "../ui/FieldLabel";
import MenuSelect from "../ui/MenuSelect";
import MutedText from "../ui/MutedText";
import OctoProprioSelector, {
  OCTO_PROPRIO_SLOTS,
  computeSelectedDriverGripperMax,
} from "./OctoProprioSelector";

const RPC_TIMEOUT_MS = 15000;

const QUALITY_PRESETS = {
  quick: { label: "Quick test", numSteps: 1000, batchSize: 8 },
  standard: { label: "Standard", numSteps: 5000, batchSize: 16 },
  high: { label: "Higher quality", numSteps: 15000, batchSize: 16 },
};

function safeJsonParse(text) {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

function buildEqualPercents(ids) {
  const out = {};
  const n = Array.isArray(ids) ? ids.length : 0;
  if (n <= 0) return out;
  const base = Math.floor(100 / n);
  let rem = 100 - base * n;
  ids.forEach((id) => {
    const key = String(id || "");
    if (!key) return;
    const bump = rem > 0 ? 1 : 0;
    if (rem > 0) rem -= 1;
    out[key] = base + bump;
  });
  return out;
}

const OctoTrainingPanel = ({
  open,
  onClose,
  onOpenTrainingMetrics,
  titleIcon,
  mobilePinPanelId,
}) => {
  const [serverUrl, setServerUrl] = useState("ws://127.0.0.1:8765");
  const [connected, setConnected] = useState(false);
  const [status, setStatus] = useState("Disconnected");
  const [error, setError] = useState("");

  const [runName, setRunName] = useState("");
  const [qualityPreset, setQualityPreset] = useState("standard");
  const [numSteps, setNumSteps] = useState(QUALITY_PRESETS.standard.numSteps);
  const [batchSize, setBatchSize] = useState(QUALITY_PRESETS.standard.batchSize);
  const [baseModel, setBaseModel] = useState("octo-small");
  const [computeDevice, setComputeDevice] = useState("auto");
  const [freezeImageTokenizer, setFreezeImageTokenizer] = useState(false);
  const [freezeTransformerBackbone, setFreezeTransformerBackbone] = useState(true);
  const [freezeDiffusionActionHead, setFreezeDiffusionActionHead] = useState(false);
  const [learningRateTokenizer, setLearningRateTokenizer] = useState("1e-5");
  const [learningRateBackbone, setLearningRateBackbone] = useState("3e-5");
  const [learningRateActionHead, setLearningRateActionHead] = useState("1e-4");

  const [datasets, setDatasets] = useState([]);
  const [datasetsLoading, setDatasetsLoading] = useState(false);
  const [selectedDatasetIds, setSelectedDatasetIds] = useState([]);
  const [mixMode, setMixMode] = useState("equal");
  const [mixPercentById, setMixPercentById] = useState({});

  const [launching, setLaunching] = useState(false);
  const [lastLaunchSummary, setLastLaunchSummary] = useState("");
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Proprio picker state
  const [proprioOpen, setProprioOpen] = useState(false);
  const [jointsData, setJointsData] = useState(null); // { joints, suggested, suggestedGripperMax, nq }
  const [jointsLoading, setJointsLoading] = useState(false);
  const [selectedQposIndices, setSelectedQposIndices] = useState([]); // up to 8 numbers
  const [gripperMax, setGripperMax] = useState(""); // raw string, empty = not set
  const lastAutoGripperMaxRef = useRef(null);
  const autoInspectAttemptedRef = useRef(false);

  const wsRef = useRef(null);
  const requestIdRef = useRef(1);
  const pendingByIdRef = useRef(new Map());

  const clearPending = useCallback((reason) => {
    const entries = Array.from(pendingByIdRef.current.entries());
    pendingByIdRef.current.clear();
    entries.forEach(([, pending]) => {
      try {
        clearTimeout(pending.timeoutId);
      } catch {
        // noop
      }
      try {
        pending.reject(new Error(reason || "WebSocket closed"));
      } catch {
        // noop
      }
    });
  }, []);

  const sendRpc = useCallback((action, payload = {}) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      return Promise.reject(new Error("Not connected to websocket agent."));
    }
    const id = `octo-train-${requestIdRef.current++}`;
    return new Promise((resolve, reject) => {
      const timeoutId = window.setTimeout(() => {
        pendingByIdRef.current.delete(id);
        reject(new Error(`Request timed out: ${action}`));
      }, RPC_TIMEOUT_MS);
      pendingByIdRef.current.set(id, { resolve, reject, timeoutId });
      try {
        ws.send(JSON.stringify({ type: "rpc_request", id, action, payload }));
      } catch (err) {
        pendingByIdRef.current.delete(id);
        window.clearTimeout(timeoutId);
        reject(err);
      }
    });
  }, []);

  const refreshDatasets = useCallback(async () => {
    if (!connected) return;
    setDatasetsLoading(true);
    try {
      const result = await sendRpc("list_octo_training_datasets", {});
      const list = Array.isArray(result?.datasets) ? result.datasets : [];
      setDatasets(list);
      setSelectedDatasetIds((prev) => {
        const valid = prev.filter((id) => list.some((d) => String(d?.id || "") === String(id)));
        if (valid.length > 0) return valid;
        return list[0]?.id ? [String(list[0].id)] : [];
      });
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setDatasetsLoading(false);
    }
  }, [connected, sendRpc]);

  const connect = () => {
    setError("");
    setStatus("Connecting...");
    try {
      wsRef.current?.close();
    } catch {
      // noop
    }
    let ws;
    try {
      ws = new WebSocket(serverUrl);
    } catch (e) {
      setStatus("Error");
      setError(String(e?.message || e));
      return;
    }
    wsRef.current = ws;
    ws.onopen = () => {
      setConnected(true);
      setStatus("Connected");
      refreshDatasets();
    };
    ws.onclose = () => {
      clearPending("WebSocket closed");
      setConnected(false);
      setStatus("Disconnected");
      setLaunching(false);
    };
    ws.onerror = () => {
      setStatus("Error");
      setError("WebSocket error. Check server URL and ensure your local agent is running.");
    };
    ws.onmessage = (ev) => {
      const msg = typeof ev.data === "string" ? safeJsonParse(ev.data) : null;
      if (!msg || msg.type !== "rpc_response" || msg.id == null) return;
      const key = String(msg.id);
      const pending = pendingByIdRef.current.get(key);
      if (!pending) return;
      pendingByIdRef.current.delete(key);
      window.clearTimeout(pending.timeoutId);
      if (msg.ok) pending.resolve(msg.result);
      else pending.reject(new Error(String(msg.error || "RPC request failed")));
    };
  };

  const disconnect = () => {
    try {
      wsRef.current?.close();
    } catch {
      // noop
    }
    wsRef.current = null;
    clearPending("WebSocket disconnected");
    setConnected(false);
    setStatus("Disconnected");
    setLaunching(false);
  };

  useEffect(() => {
    return () => {
      try {
        wsRef.current?.close();
      } catch {
        // noop
      }
      clearPending("Panel unmounted");
    };
  }, [clearPending]);

  const selectedDatasetRows = useMemo(
    () => datasets.filter((d) => selectedDatasetIds.includes(String(d?.id || ""))),
    [datasets, selectedDatasetIds]
  );

  useEffect(() => {
    const ids = selectedDatasetRows.map((row) => String(row?.id || "")).filter(Boolean);
    setMixPercentById((prev) => {
      if (ids.length === 0) return {};
      const current = {};
      let total = 0;
      ids.forEach((id) => {
        const pct = Number(prev[id]);
        if (Number.isFinite(pct) && pct >= 0) {
          current[id] = pct;
          total += pct;
        }
      });
      if (!(total > 0)) return buildEqualPercents(ids);

      const next = {};
      let assigned = 0;
      ids.forEach((id, idx) => {
        if (idx === ids.length - 1) {
          next[id] = Math.max(0, 100 - assigned);
          return;
        }
        const pct = Math.max(0, Math.floor((current[id] / total) * 100));
        next[id] = pct;
        assigned += pct;
      });
      return next;
    });
  }, [selectedDatasetRows]);

  const setCustomPercent = (targetId, rawValue) => {
    const ids = selectedDatasetRows.map((row) => String(row?.id || "")).filter(Boolean);
    if (!ids.includes(targetId)) return;
    if (ids.length === 1) {
      setMixPercentById({ [targetId]: 100 });
      return;
    }
    const v = Math.max(0, Math.min(100, Math.floor(Number(rawValue) || 0)));
    const others = ids.filter((id) => id !== targetId);
    const remainder = 100 - v;

    setMixPercentById((prev) => {
      const next = { ...prev, [targetId]: v };
      const othersSum = others.reduce((acc, id) => acc + Math.max(0, Number(prev[id]) || 0), 0);
      if (othersSum <= 0) {
        const even = Math.floor(remainder / others.length);
        let rem = remainder - even * others.length;
        others.forEach((id) => {
          const bump = rem > 0 ? 1 : 0;
          if (rem > 0) rem -= 1;
          next[id] = even + bump;
        });
        return next;
      }

      let assigned = 0;
      others.forEach((id, idx) => {
        if (idx === others.length - 1) return;
        const pct = Math.max(0, Math.floor(((Number(prev[id]) || 0) / othersSum) * remainder));
        next[id] = pct;
        assigned += pct;
      });
      next[others[others.length - 1]] = Math.max(0, remainder - assigned);
      return next;
    });
  };

  const toggleDataset = (datasetId, checked) => {
    const id = String(datasetId || "");
    if (!id) return;
    setSelectedDatasetIds((prev) => {
      if (checked) return prev.includes(id) ? prev : [...prev, id];
      return prev.filter((x) => x !== id);
    });
  };

  const inspectJoints = useCallback(async () => {
    if (!connected) return;
    setJointsLoading(true);
    setError("");
    try {
      const result = await sendRpc("inspect_octo_joints");
      setJointsData(result);
      setSelectedQposIndices(Array.isArray(result?.suggested) ? result.suggested.map(Number) : []);
      if (result?.suggestedGripperMax != null) {
        setGripperMax(String(result.suggestedGripperMax));
      }
      setProprioOpen(true);
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setJointsLoading(false);
    }
  }, [connected, sendRpc]);

  useEffect(() => {
    if (!connected) {
      autoInspectAttemptedRef.current = false;
      return;
    }
    if (autoInspectAttemptedRef.current) return;
    if (jointsLoading) return;
    if (selectedQposIndices.length > 0 || String(gripperMax || "").trim()) {
      autoInspectAttemptedRef.current = true;
      return;
    }
    autoInspectAttemptedRef.current = true;
    setJointsLoading(true);
    sendRpc("inspect_octo_joints")
      .then((result) => {
        setJointsData(result || null);
        const suggested = Array.isArray(result?.suggested)
          ? result.suggested
              .map(Number)
              .filter((v) => Number.isFinite(v) && v >= 0)
              .slice(0, OCTO_PROPRIO_SLOTS)
          : [];
        if (suggested.length > 0) setSelectedQposIndices(suggested);
        if (!String(gripperMax || "").trim() && result?.suggestedGripperMax != null) {
          setGripperMax(String(result.suggestedGripperMax));
        }
      })
      .catch(() => {
        // Keep this quiet; users can still click "Inspect joints" manually.
      })
      .finally(() => {
        setJointsLoading(false);
      });
  }, [connected, gripperMax, jointsLoading, selectedQposIndices.length, sendRpc]);

  const toggleJoint = useCallback((qposIdx) => {
    setSelectedQposIndices((prev) => {
      const idx = Number(qposIdx);
      if (prev.includes(idx)) return prev.filter((x) => x !== idx);
      if (prev.length >= OCTO_PROPRIO_SLOTS) return prev; // already full
      return [...prev, idx];
    });
  }, []);

  const autoFillGripperMax = useCallback(
    (force = true) => {
      const next = computeSelectedDriverGripperMax(jointsData, selectedQposIndices);
      if (!(next != null && Number.isFinite(next))) return;
      const current = Number.parseFloat(String(gripperMax || ""));
      const prevAuto = Number(lastAutoGripperMaxRef.current);
      const currentIsPrevAuto =
        Number.isFinite(current) &&
        Number.isFinite(prevAuto) &&
        Math.abs(current - prevAuto) < 1e-9;
      if (force || gripperMax === "" || currentIsPrevAuto) {
        setGripperMax(String(next));
        lastAutoGripperMaxRef.current = next;
      }
    },
    [gripperMax, jointsData, selectedQposIndices]
  );

  useEffect(() => {
    autoFillGripperMax(false);
  }, [autoFillGripperMax, jointsData, selectedQposIndices]);

  const handleQualityPresetChange = (preset) => {
    const key = String(preset || "");
    setQualityPreset(key);
    const row = QUALITY_PRESETS[key];
    if (!row) return;
    setNumSteps(row.numSteps);
    setBatchSize(row.batchSize);
  };

  const startTraining = async () => {
    setError("");
    setLastLaunchSummary("");
    const trimmedRunName = String(runName || "").trim();
    let datasetRowsForRun = [...selectedDatasetRows];
    if (datasetRowsForRun.length === 0) {
      setError("Select at least one dataset before starting.");
      return;
    }
    const steps = Math.max(1, Math.floor(Number(numSteps) || 0));
    const batch = Math.max(1, Math.floor(Number(batchSize) || 0));
    if (!Number.isFinite(steps) || !Number.isFinite(batch)) {
      setError("Steps and batch size must be valid numbers.");
      return;
    }

    let datasetWeights = null;
    if (datasetRowsForRun.length > 1) {
      if (mixMode === "custom") {
        datasetRowsForRun = datasetRowsForRun.filter(
          (row) => Math.max(0, Math.floor(Number(mixPercentById[String(row?.id || "")]) || 0)) > 0
        );
        if (datasetRowsForRun.length === 0) {
          setError("At least one dataset must be above 0%.");
          return;
        }
        datasetWeights = datasetRowsForRun.map((row) =>
          Math.max(0, Math.floor(Number(mixPercentById[String(row?.id || "")]) || 0))
        );
      } else {
        datasetWeights = datasetRowsForRun.map(() => 1);
      }
    }

    const datasetPaths = datasetRowsForRun
      .map((row) => String(row?.path || "").trim())
      .filter(Boolean);
    if (datasetPaths.length === 0) {
      setError("Selected datasets do not contain valid paths.");
      return;
    }

    const parsedLrTokenizer = Number(learningRateTokenizer);
    const parsedLrBackbone = Number(learningRateBackbone);
    const parsedLrActionHead = Number(learningRateActionHead);
    if (!freezeImageTokenizer && !(Number.isFinite(parsedLrTokenizer) && parsedLrTokenizer > 0)) {
      setError("Tokenizer learning rate must be a positive number when tokenizer is unfrozen.");
      return;
    }
    if (
      !freezeTransformerBackbone &&
      !(Number.isFinite(parsedLrBackbone) && parsedLrBackbone > 0)
    ) {
      setError("Backbone learning rate must be a positive number when backbone is unfrozen.");
      return;
    }
    if (
      !freezeDiffusionActionHead &&
      !(Number.isFinite(parsedLrActionHead) && parsedLrActionHead > 0)
    ) {
      setError("Action-head learning rate must be a positive number when action head is unfrozen.");
      return;
    }

    setLaunching(true);
    try {
      const proprioIndicesStr =
        selectedQposIndices.length > 0 ? selectedQposIndices.join(",") : null;
      const parsedGripperMax = gripperMax !== "" ? parseFloat(gripperMax) : null;

      const result = await sendRpc("start_octo_finetune", {
        runName: trimmedRunName,
        qualityPreset,
        numSteps: steps,
        batchSize: batch,
        baseModel,
        computeDevice,
        freezeImageTokenizer,
        freezeTransformerBackbone,
        freezeDiffusionActionHead,
        learningRateTokenizer: freezeImageTokenizer ? 0 : parsedLrTokenizer,
        learningRateBackbone: freezeTransformerBackbone ? 0 : parsedLrBackbone,
        learningRateActionHead: freezeDiffusionActionHead ? 0 : parsedLrActionHead,
        datasetPaths,
        datasetWeights,
        octoProprioIndices: proprioIndicesStr,
        octoGripperMax:
          parsedGripperMax != null && Number.isFinite(parsedGripperMax) ? parsedGripperMax : null,
      });
      const summaryLines = [];
      if (result?.runLabel) summaryLines.push(`Run: ${result.runLabel}`);
      if (result?.saveDir) summaryLines.push(`Save dir: ${result.saveDir}`);
      if (result?.pid != null) summaryLines.push(`PID: ${result.pid}`);
      if (result?.command) summaryLines.push(`Command: ${result.command}`);
      setLastLaunchSummary(summaryLines.join("\n"));
      setStatus("Training launched");
    } catch (err) {
      setError(String(err?.message || err));
      setStatus("Error");
    } finally {
      setLaunching(false);
    }
  };

  const customTotal = selectedDatasetRows.reduce(
    (acc, row) => acc + Math.max(0, Math.floor(Number(mixPercentById[String(row?.id || "")]) || 0)),
    0
  );

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Octo Training"
      titleIcon={titleIcon}
      minWidth={380}
      minHeight={420}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="flex-1 overflow-auto p-3 space-y-3">
        <FieldLabel>
          Start Octo fine-tuning from inside the app. The run output folder is generated
          automatically from your run name.
        </FieldLabel>

        <div className="border border-gray-700 rounded-md bg-gray-900/30 p-3 space-y-2">
          <FieldLabel as="label" className="block text-gray-300">
            Server URL
            <input
              className="mt-1 w-full px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-100"
              type="text"
              value={serverUrl}
              disabled={connected}
              onChange={(e) => setServerUrl(e.target.value)}
            />
          </FieldLabel>
          <div className="flex items-center gap-2">
            <button
              className="px-3 py-1 rounded border border-blue-600 text-blue-200 hover:bg-blue-900 disabled:opacity-50"
              disabled={connected}
              onClick={connect}
            >
              Connect
            </button>
            <button
              className="px-3 py-1 rounded border border-gray-600 text-gray-200 hover:bg-gray-700 disabled:opacity-50"
              disabled={!connected}
              onClick={disconnect}
            >
              Disconnect
            </button>
            <button
              className="px-3 py-1 rounded border border-gray-600 text-gray-200 hover:bg-gray-700 disabled:opacity-50"
              disabled={!connected || datasetsLoading}
              onClick={refreshDatasets}
            >
              {datasetsLoading ? "Refreshing..." : "Refresh datasets"}
            </button>
            <FieldLabel className="text-[11px]">{status}</FieldLabel>
          </div>
        </div>

        <FieldLabel as="label" className="block text-gray-300">
          Run name
          <input
            className="mt-1 w-full px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-100"
            type="text"
            value={runName}
            onChange={(e) => setRunName(e.target.value)}
            placeholder="e.g. pick-and-place-v1"
          />
        </FieldLabel>

        <div className="border border-gray-700 rounded-md bg-gray-900/30 p-3 space-y-2">
          <FieldLabel className="text-gray-300">Datasets (select one or more)</FieldLabel>
          <div className="max-h-40 overflow-auto space-y-1">
            {datasets.length === 0 ? (
              <MutedText className="text-[11px]">No datasets found.</MutedText>
            ) : (
              datasets.map((row) => {
                const id = String(row?.id || "");
                const checked = selectedDatasetIds.includes(id);
                const label = String(row?.label || row?.id || "Dataset");
                const path = String(row?.path || "");
                return (
                  <Checkbox
                    key={id}
                    checked={checked}
                    onChange={(e) => toggleDataset(id, e.target.checked)}
                    label={
                      <span className="min-w-0">
                        <span className="block text-gray-200 truncate">{label}</span>
                        <span className="block panel-text-helper truncate">{path}</span>
                      </span>
                    }
                    className="text-[11px] text-gray-300 border border-gray-800 rounded px-2 py-1 bg-gray-950/30"
                  />
                );
              })
            )}
          </div>
          <MutedText className="text-[11px]">
            {selectedDatasetRows.length} selected{" "}
            {selectedDatasetRows.length === 1 ? "dataset" : "datasets"}.
          </MutedText>
        </div>

        {selectedDatasetRows.length > 1 ? (
          <div className="border border-gray-700 rounded-md bg-gray-900/30 p-3 space-y-2">
            <FieldLabel className="text-gray-300">Mixing</FieldLabel>
            <FieldLabel as="label" className="text-[11px] text-gray-300 block">
              Mix mode
              <MenuSelect
                size="compact"
                className="mt-1 w-full"
                value={mixMode}
                onChange={(v) => setMixMode(String(v || "equal"))}
                options={[
                  { value: "equal", label: "Equal mix" },
                  { value: "custom", label: "Custom proportions (sliders)" },
                ]}
                triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
                aria-label="Dataset mix mode"
              />
            </FieldLabel>
            {mixMode === "custom" ? (
              <div className="space-y-1">
                {selectedDatasetRows.map((row) => {
                  const id = String(row?.id || "");
                  const label = String(row?.label || row?.id || "Dataset");
                  const pct = Math.max(0, Math.floor(Number(mixPercentById[id]) || 0));
                  return (
                    <div
                      key={`w-${id}`}
                      className="text-[11px] text-gray-300 flex items-center gap-2"
                    >
                      <span className="w-28 truncate">{label}</span>
                      <input
                        className="flex-1"
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={pct}
                        onChange={(e) => setCustomPercent(id, e.target.value)}
                      />
                      <input
                        className="w-16 px-1 py-0.5 rounded bg-gray-800 border border-gray-700 text-gray-100 text-right tabular-nums"
                        type="number"
                        min={0}
                        max={100}
                        step={1}
                        value={pct}
                        onChange={(e) => setCustomPercent(id, e.target.value)}
                      />
                      <span className="w-4 text-gray-400">%</span>
                    </div>
                  );
                })}
                <MutedText className="text-[11px]">Total: {customTotal}%</MutedText>
              </div>
            ) : (
              <MutedText className="text-[11px]">
                All selected datasets are sampled equally.
              </MutedText>
            )}
          </div>
        ) : null}

        <OctoProprioSelector
          open={proprioOpen}
          onOpenChange={setProprioOpen}
          connected={connected}
          jointsLoading={jointsLoading}
          onInspect={inspectJoints}
          jointsData={jointsData}
          selectedQposIndices={selectedQposIndices}
          onToggleJoint={toggleJoint}
          gripperMax={gripperMax}
          onGripperMaxChange={setGripperMax}
          onAutoFillGripperMax={autoFillGripperMax}
          maxSlots={OCTO_PROPRIO_SLOTS}
          emptyStateText={
            'Click "Inspect joints" to load joint data from the project archive. Select up to 8 joints to use as Octo\'s proprioceptive input.'
          }
          emptyIndicesText="(none - will use first 8 obs dims)"
          noActuatorsText="No actuators found - select joints below manually."
        />

        <div className="grid grid-cols-3 gap-2">
          <FieldLabel as="label" className="block text-gray-300">
            Training quality
            <MenuSelect
              size="compact"
              className="mt-1 w-full"
              value={qualityPreset}
              onChange={handleQualityPresetChange}
              options={Object.entries(QUALITY_PRESETS).map(([id, row]) => ({
                value: id,
                label: row.label,
              }))}
              triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
              aria-label="Training quality"
            />
          </FieldLabel>
          <FieldLabel as="label" className="block text-gray-300">
            Steps
            <input
              className="mt-1 w-full px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-100"
              type="number"
              min={1}
              step={1}
              value={numSteps}
              onChange={(e) => setNumSteps(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
            />
          </FieldLabel>
          <FieldLabel as="label" className="block text-gray-300">
            Batch size
            <input
              className="mt-1 w-full px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-100"
              type="number"
              min={1}
              step={1}
              value={batchSize}
              onChange={(e) => setBatchSize(Math.max(1, Math.floor(Number(e.target.value) || 1)))}
            />
          </FieldLabel>
        </div>

        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <div className="border border-gray-700 rounded-md bg-gray-900/30 p-3">
            <CollapsibleTrigger className="w-full text-xs text-gray-200 justify-between">
              Advanced
            </CollapsibleTrigger>
            <CollapsibleContent>
              <FieldLabel as="label" className="block mt-2 text-gray-300">
                Base model
                <MenuSelect
                  size="compact"
                  className="mt-1 w-full"
                  value={baseModel}
                  onChange={setBaseModel}
                  options={[
                    { value: "octo-small", label: "Octo small (recommended)" },
                    { value: "octo-base", label: "Octo base" },
                  ]}
                  triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
                  aria-label="Base model"
                />
              </FieldLabel>
              <FieldLabel as="label" className="block mt-2 text-gray-300">
                Compute device
                <MenuSelect
                  size="compact"
                  className="mt-1 w-full"
                  value={computeDevice}
                  onChange={(v) => setComputeDevice(String(v || "auto"))}
                  options={[
                    { value: "auto", label: "Auto (use GPU if available)" },
                    { value: "gpu", label: "GPU" },
                    { value: "cpu", label: "CPU" },
                  ]}
                  triggerProps={{ onPointerDown: (e) => e.stopPropagation() }}
                  aria-label="Compute device"
                />
              </FieldLabel>
              <div className="mt-3 border border-gray-700 rounded-md bg-gray-950/40 p-2 space-y-2">
                <FieldLabel className="text-[11px] text-gray-300">
                  Freeze components (Image tokenizer {"->"} Octo transformer backbone {"->"}{" "}
                  Diffusion action head)
                </FieldLabel>
                <Checkbox
                  label="Freeze Image tokenizer"
                  checked={freezeImageTokenizer}
                  onChange={(e) => setFreezeImageTokenizer(Boolean(e.target.checked))}
                  className="text-[11px] text-gray-300 w-auto px-0 py-0"
                />
                <FieldLabel as="label" className="text-[11px] text-gray-300 block">
                  Tokenizer learning rate
                  <input
                    className="mt-1 w-full px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-100 disabled:opacity-50"
                    type="number"
                    min={0}
                    step="any"
                    value={learningRateTokenizer}
                    disabled={freezeImageTokenizer}
                    onChange={(e) => setLearningRateTokenizer(String(e.target.value))}
                  />
                </FieldLabel>
                <Checkbox
                  label="Freeze Octo transformer backbone"
                  checked={freezeTransformerBackbone}
                  onChange={(e) => setFreezeTransformerBackbone(Boolean(e.target.checked))}
                  className="text-[11px] text-gray-300 w-auto px-0 py-0"
                />
                <FieldLabel as="label" className="text-[11px] text-gray-300 block">
                  Backbone learning rate
                  <input
                    className="mt-1 w-full px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-100 disabled:opacity-50"
                    type="number"
                    min={0}
                    step="any"
                    value={learningRateBackbone}
                    disabled={freezeTransformerBackbone}
                    onChange={(e) => setLearningRateBackbone(String(e.target.value))}
                  />
                </FieldLabel>
                <Checkbox
                  label="Freeze Diffusion action head"
                  checked={freezeDiffusionActionHead}
                  onChange={(e) => setFreezeDiffusionActionHead(Boolean(e.target.checked))}
                  className="text-[11px] text-gray-300 w-auto px-0 py-0"
                />
                <FieldLabel as="label" className="text-[11px] text-gray-300 block">
                  Action-head learning rate
                  <input
                    className="mt-1 w-full px-2 py-1 rounded bg-gray-800 border border-gray-700 text-gray-100 disabled:opacity-50"
                    type="number"
                    min={0}
                    step="any"
                    value={learningRateActionHead}
                    disabled={freezeDiffusionActionHead}
                    onChange={(e) => setLearningRateActionHead(String(e.target.value))}
                  />
                </FieldLabel>
              </div>
            </CollapsibleContent>
          </div>
        </Collapsible>

        <button
          className="w-full px-3 py-2 rounded border border-green-600 text-green-200 hover:bg-green-900 disabled:opacity-50"
          disabled={!connected || launching}
          onClick={startTraining}
        >
          {launching ? "Starting training..." : "Start training"}
        </button>

        {lastLaunchSummary ? (
          <div className="space-y-2">
            <FieldLabel className="text-[11px] whitespace-pre-wrap">{lastLaunchSummary}</FieldLabel>
            <button
              className="px-3 py-1 rounded border border-blue-600 text-blue-200 hover:bg-blue-900 disabled:opacity-50 text-xs"
              onClick={() => onOpenTrainingMetrics?.()}
              disabled={typeof onOpenTrainingMetrics !== "function"}
            >
              Open Training Metrics
            </button>
          </div>
        ) : null}
        {error ? <div className="text-[11px] text-red-300 whitespace-pre-wrap">{error}</div> : null}
      </div>
    </DraggablePanel>
  );
};

export default OctoTrainingPanel;
